import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/api";
import { Star, Quote } from "lucide-react";

const fmonth = (s) => (s ? new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : "");
const Stars = ({ n, size = "h-4 w-4" }) => (
  <span className="inline-flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`${size} ${i <= (n || 0) ? "fill-[#FFD700] text-[#FFD700]" : "text-[#DEE2E6]"}`} />)}</span>
);
const fmtAvg = (a) => (a == null ? "" : String(a).replace(".", ","));

function useSiteReviews(code, limit) {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/public/sites/${code}/reviews${limit ? `?limit=${limit}` : ""}`).then((r) => setData(r.data)).catch(() => setData(null)); }, [code, limit]);
  return data;
}

export const scrollToReviews = () => document.getElementById("avis")?.scrollIntoView({ behavior: "smooth", block: "start" });

// Badge « ★ 4,8/5 · 12 avis » pour le hero : masqué tant qu'aucun avis n'est publié
export function MiniSiteRatingBadge({ code, onClick, dark = true }) {
  const data = useSiteReviews(code, 1);
  if (!data || !data.stats.count) return null;
  const { count, average } = data.stats;
  return (
    <button type="button" onClick={onClick || scrollToReviews} data-testid="minisite-rating-badge" aria-label={`Note moyenne ${fmtAvg(average)} sur 5, ${count} avis`}
      className={`inline-flex items-center gap-2.5 border px-3.5 py-2 text-left transition-colors ${dark ? "border-white/30 bg-white/10 hover:bg-white/20 text-white" : "border-[#DEE2E6] bg-white hover:border-[#0A0A0A] text-[#0A0A0A]"}`}>
      <Star className="h-4 w-4 fill-[#FFD700] text-[#FFD700]" />
      <span className="font-display font-black text-lg leading-none" data-testid="minisite-rating-average">{fmtAvg(average)}<span className={`text-xs font-bold ${dark ? "text-white/60" : "text-[#868E96]"}`}>/5</span></span>
      <span className={`text-[11px] uppercase tracking-[0.15em] font-bold ${dark ? "text-white/70" : "text-[#495057]"}`} data-testid="minisite-rating-count">{count} avis vérifié{count > 1 ? "s" : ""}</span>
    </button>
  );
}

// Phase 56 — avis publiés par le gérant, affichés sur le mini-site public
export function MiniSiteReviews({ code, accent = "#002FA7" }) {
  const data = useSiteReviews(code);
  useEffect(() => { if (data?.stats?.count && window.location.hash === "#avis") setTimeout(scrollToReviews, 150); }, [data]);
  if (!data || !data.stats.count) return null;
  const { stats, items } = data;
  return (
    <section id="avis" className="border-t border-[#DEE2E6] bg-white scroll-mt-4" data-testid="minisite-reviews">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: accent }}>// Avis de nos visiteurs</div>
            <h2 className="mt-2 font-display text-2xl lg:text-3xl font-black tracking-tight">Ils ont séjourné chez nous</h2>
          </div>
          <div className="flex items-center gap-4 border border-[#DEE2E6] px-5 py-3" data-testid="minisite-reviews-stats">
            <div className="font-display text-4xl font-black tracking-tighter">{fmtAvg(stats.average)}<span className="text-lg text-[#868E96]">/5</span></div>
            <div><Stars n={Math.round(stats.average)} size="h-5 w-5" /><div className="text-[11px] text-[#868E96] mt-0.5">{stats.count} avis vérifié{stats.count > 1 ? "s" : ""} · séjours réels</div></div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {items.map((r) => (
            <article key={r.id} className="border border-[#DEE2E6] p-6 flex flex-col" data-testid={`minisite-review-${r.id}`}>
              <div className="flex items-center justify-between"><Stars n={r.rating} /><Quote className="h-5 w-5 text-[#DEE2E6]" /></div>
              {r.comment && <p className="text-sm text-[#212529] mt-4 flex-1 leading-relaxed">« {r.comment} »</p>}
              <div className="mt-4 text-[11px] text-[#868E96]"><b className="text-[#0A0A0A]">{r.author}</b> · {r.unit_label} · {fmonth(r.stayed)}</div>
              {r.reply && <div className="mt-3 border-l-2 pl-3 text-xs" style={{ borderColor: accent }}><div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>Réponse de l'établissement</div><p className="text-[#495057] mt-1">{r.reply}</p></div>}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
